import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { Badge, EmptyState, Input, PageHeader, Spinner } from "@magarac/ui";
import { searchResponseSchema, type SearchResult } from "@magarac/shared/schemas/search";
import { HelpIcon } from "../../components/HelpIcon";
import { api } from "../../lib/api";
import { useTenant } from "../../lib/tenant";

export const Route = createFileRoute("/_app/search")({
  // The router reads "?q=5550123" as a number; phone searches are digits, so accept both.
  validateSearch: z.object({ q: z.union([z.string(), z.number()]).transform(String).optional() }),
  component: SearchPage,
});

const TYPE_LABEL: Record<SearchResult["type"], string> = {
  customer: "Customer",
  property: "Address",
  job: "Job",
  request: "Request",
};

/** CRM-4. Screen key: search */
function SearchPage() {
  const { q: initial = "" } = Route.useSearch();
  const navigate = useNavigate();
  const { active } = useTenant();
  const [q, setQ] = useState(initial);
  const [debounced, setDebounced] = useState(initial);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    void navigate({ to: "/search", search: { q: debounced || undefined }, replace: true });
  }, [debounced, navigate]);

  const results = useQuery({
    queryKey: ["search", active?.id, debounced],
    queryFn: () => api.get("/search", searchResponseSchema, { query: { q: debounced } }),
    enabled: Boolean(active) && debounced.length > 0,
  });

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader title="Search" help={<HelpIcon screen="search" />} />
      <label htmlFor="search-page" className="sr-only">
        Search customers, addresses, jobs, and requests
      </label>
      <Input
        id="search-page"
        type="search"
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Name, phone, address, or job number"
      />
      <div className="mt-4">
        {results.isFetching ? <Spinner /> : null}
        {results.data && results.data.results.length === 0 ? (
          <EmptyState
            title="Nothing found"
            body="Try part of a name, the last 4 digits of a phone, or a street name."
          />
        ) : null}
        <ul className="divide-y divide-line rounded-card border border-line bg-surface empty:hidden">
          {results.data?.results.map((r) => (
            <li key={`${r.type}:${r.id}`}>
              <Link
                to={linkFor(r).to}
                params={linkFor(r).params}
                className="flex items-center justify-between gap-3 p-4 hover:bg-brand-50"
              >
                <div className="min-w-0">
                  <div className="truncate font-medium">{r.title}</div>
                  {r.subtitle ? (
                    <div className="truncate text-sm text-muted">{r.subtitle}</div>
                  ) : null}
                </div>
                <Badge>{TYPE_LABEL[r.type]}</Badge>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function linkFor(r: SearchResult): { to: string; params: Record<string, string> } {
  switch (r.type) {
    case "job":
      return { to: "/jobs/$id", params: { id: r.id } };
    case "request":
      return { to: "/requests", params: {} };
    default:
      return { to: "/customers/$id", params: { id: r.customerId ?? r.id } };
  }
}
